/**
 * FERP Portal - Real-Time Subscription Hooks
 *
 * Provides hooks for GraphQL subscriptions with:
 * - Auto-reconnection via graphql-ws (configured in apollo-client.ts)
 * - Connection status tracking
 * - Event buffering with configurable max size (backpressure)
 * - Life-critical update prioritization
 *
 * CONSTRAINTS:
 * - All subscriptions are tenant-scoped (AuthCtx on backend)
 * - WebSocket reconnects with exponential backoff (apollo-client.ts)
 * - Buffer size limits prevent memory exhaustion
 *
 * Operations are defined in subscriptions.graphql and match backend resolvers
 * in graphql_gateway/src/forms_api/graphql/. After codegen, import generated
 * document constants instead of inline gql.
 */

import { useState, useCallback, useRef } from 'react';
// eslint-disable-next-line no-restricted-imports -- DATA-LAYER hook, allowed to use Apollo Client directly
import { useSubscription } from '@apollo/client/react';
import { gql } from '@apollo/client';
import type {
  TaskStatusUpdate,
  TaskAssignmentEvent,
  NotificationEvent,
  SafetyEventNotification,
  SagaStateTransitionEvent,
  AuditEvent,
} from '@/generated/graphql';

// ============================================================================
// SHARED CONSTANTS
// ============================================================================

/** Maximum events to buffer per subscription before dropping oldest */
const DEFAULT_BUFFER_SIZE = 100;

// ============================================================================
// TYPES
// ============================================================================

export interface RealTimeTaskUpdate {
  type: 'assigned' | 'status_changed';
  data: TaskStatusUpdate | TaskAssignmentEvent;
  isLifeCritical: boolean;
  timestamp: Date;
}

export interface RealTimeAlertUpdate {
  type: 'created' | 'escalated';
  alert: SafetyEventNotification;
  isCritical: boolean;
  timestamp: Date;
}

export interface RealTimeNotification {
  notification: NotificationEvent;
  timestamp: Date;
}

// ============================================================================
// SUBSCRIPTION DOCUMENTS
// Inline gql matching subscriptions.graphql operations.
// After codegen, replace with imported document constants.
// ============================================================================

const TASK_STATUS_UPDATED_SUBSCRIPTION = gql`
  subscription OnTaskStatusUpdate($taskId: String, $instanceId: String) {
    taskStatusUpdates(taskId: $taskId, instanceId: $instanceId) {
      taskId
      oldStatus
      newStatus
      updatedBy
      reason
      instanceId
      tenant
      timestamp
    }
  }
`;

const SAFETY_EVENT_CREATED_SUBSCRIPTION = gql`
  subscription OnSafetyEventCreated(
    $tenant: String!
    $severityThreshold: String
    $categoryFilter: String
    $emergencyOnly: Boolean
  ) {
    safetyEventCreated(
      tenant: $tenant
      severityThreshold: $severityThreshold
      categoryFilter: $categoryFilter
      emergencyOnly: $emergencyOnly
    ) {
      eventId
      tenant
      category
      severity
      eventType
      description
      safetyScore
      remediationRequired
      escalationTriggered
      emergencyContactsNotified
      userId
      auditTrailId
      timestamp
    }
  }
`;

const NOTIFICATION_SUBSCRIPTION = gql`
  subscription OnNotification($channels: [String!], $severityThreshold: String) {
    notifications(channels: $channels, severityThreshold: $severityThreshold) {
      id
      title
      message
      severity
      channel
      data
      timestamp
    }
  }
`;

const SAGA_STATE_TRANSITION_SUBSCRIPTION = gql`
  subscription OnSagaStateTransition(
    $tenant: String!
    $sagaExecutionId: String
    $primitiveTypes: [String!]
  ) {
    sagaStateTransitions(
      tenant: $tenant
      sagaExecutionId: $sagaExecutionId
      primitiveTypes: $primitiveTypes
    ) {
      eventId
      sagaExecutionId
      primitiveType
      fromState
      toState
      stepId
      executionResult
      safetyScore
      performanceMetrics
      eventType
      userId
      tenant
      auditTrailId
      timestamp
    }
  }
`;

const AUDIT_EVENT_SUBSCRIPTION = gql`
  subscription OnAuditEvent($tenant: String!, $eventTypes: [String!]) {
    auditEvents(tenant: $tenant, eventTypes: $eventTypes) {
      auditId
      entityType
      entityId
      eventType
      action
      actorId
      actorType
      outcome
      immutableHash
      details
      timestamp
      requestId
      sessionId
      ipAddress
      complianceTags
    }
  }
`;

const WORKFLOW_EVENT_SUBSCRIPTION = gql`
  subscription OnWorkflowEvent(
    $tenant: String!
    $workflowName: String
    $instanceId: String
  ) {
    workflowEvents(
      tenant: $tenant
      workflowName: $workflowName
      instanceId: $instanceId
    ) {
      eventId
      eventType
      primitiveType
      fromState
      toState
      stepId
      executionResult
      safetyScore
      performanceMetrics
      userId
      tenant
      auditTrailId
      timestamp
    }
  }
`;

// =============================================================================
// ACTOR SUBSCRIPTIONS (actor_api/actor_graphql.rs)
// Real-time actor status, task assignments, circuit breakers, workload
// =============================================================================

const ACTOR_STATUS_CHANGED_SUBSCRIPTION = gql`
  subscription OnActorStatusChanged($actorId: String!) {
    actorStatusChanged(actorId: $actorId) {
      id
      name
      actorType
      active
      available
      workload
      maxCapacity
      circuitBreakerOpen
      teamId
      updatedAt
    }
  }
`;

const ACTOR_TASK_ASSIGNED_SUBSCRIPTION = gql`
  subscription OnActorTaskAssigned($actorId: String!) {
    actorTaskAssigned(actorId: $actorId) {
      id
      name
      actorType
      active
      available
      workload
      maxCapacity
      circuitBreakerOpen
      teamId
      updatedAt
    }
  }
`;

const ACTOR_CIRCUIT_BREAKER_TRIGGERED_SUBSCRIPTION = gql`
  subscription OnActorCircuitBreakerTriggered {
    actorCircuitBreakerTriggered {
      id
      name
      actorType
      active
      available
      workload
      maxCapacity
      circuitBreakerOpen
      circuitBreakerThreshold
      teamId
      updatedAt
    }
  }
`;

const ACTOR_WORKLOAD_CHANGED_SUBSCRIPTION = gql`
  subscription OnActorWorkloadChanged {
    actorWorkloadChanged {
      id
      name
      actorType
      active
      available
      workload
      maxCapacity
      circuitBreakerOpen
      teamId
      updatedAt
    }
  }
`;

// ============================================================================
// BUFFERED EVENT HOOK (shared logic)
// ============================================================================

function useEventBuffer<T>(maxSize: number = DEFAULT_BUFFER_SIZE) {
  const [events, setEvents] = useState<T[]>([]);
  const bufferRef = useRef<T[]>([]);

  const push = useCallback(
    (event: T) => {
      bufferRef.current = [...bufferRef.current, event];
      // Backpressure: drop oldest when buffer full
      if (bufferRef.current.length > maxSize) {
        bufferRef.current = bufferRef.current.slice(-maxSize);
      }
      setEvents([...bufferRef.current]);
    },
    [maxSize],
  );

  const clear = useCallback(() => {
    bufferRef.current = [];
    setEvents([]);
  }, []);

  return { events, push, clear, latest: events[events.length - 1] ?? null };
}

// ============================================================================
// TASK SUBSCRIPTIONS
// ============================================================================

interface UseTaskSubscriptionsOptions {
  taskId?: string;
  instanceId?: string;
  onTaskUpdate?: (update: RealTimeTaskUpdate) => void;
  skip?: boolean;
  bufferSize?: number;
}

/**
 * Subscribe to task status updates in real-time.
 *
 * Matches backend resolver: TaskSubscription::task_status_updates
 * Uses Apollo useSubscription with graphql-ws WebSocket link.
 */
export function useTaskSubscriptions(options: UseTaskSubscriptionsOptions = {}) {
  const { taskId, instanceId, onTaskUpdate, skip = false, bufferSize } = options;
  const buffer = useEventBuffer<RealTimeTaskUpdate>(bufferSize);

  const { loading, error } = useSubscription<{
    taskStatusUpdates: TaskStatusUpdate;
  }>(TASK_STATUS_UPDATED_SUBSCRIPTION, {
    variables: { taskId, instanceId },
    skip,
    onData: ({ data: result }) => {
      if (result.data?.taskStatusUpdates) {
        const update: RealTimeTaskUpdate = {
          type: 'status_changed',
          data: result.data.taskStatusUpdates,
          isLifeCritical: false,
          timestamp: new Date(),
        };
        buffer.push(update);
        onTaskUpdate?.(update);
      }
    },
  });

  return {
    updates: buffer.events,
    clearUpdates: buffer.clear,
    latestUpdate: buffer.latest,
    lifeCriticalCount: buffer.events.filter((u) => u.isLifeCritical).length,
    error: error ?? null,
    isConnected: !loading && !error,
  };
}

// ============================================================================
// ALERT / SAFETY EVENT SUBSCRIPTIONS
// ============================================================================

interface UseAlertSubscriptionsOptions {
  tenant?: string;
  severityThreshold?: string;
  categoryFilter?: string;
  emergencyOnly?: boolean;
  onAlertUpdate?: (update: RealTimeAlertUpdate) => void;
  skip?: boolean;
  bufferSize?: number;
}

/**
 * Subscribe to safety event alerts in real-time.
 *
 * Matches backend resolver: FormsSubscription::safety_event_created
 * Life-critical: <1ms delivery target for critical events.
 */
export function useAlertSubscriptions(options: UseAlertSubscriptionsOptions = {}) {
  const {
    tenant,
    severityThreshold,
    categoryFilter,
    emergencyOnly,
    onAlertUpdate,
    skip = false,
    bufferSize,
  } = options;
  const buffer = useEventBuffer<RealTimeAlertUpdate>(bufferSize);

  const { loading, error } = useSubscription<{
    safetyEventCreated: SafetyEventNotification;
  }>(SAFETY_EVENT_CREATED_SUBSCRIPTION, {
    variables: { tenant: tenant ?? '', severityThreshold, categoryFilter, emergencyOnly },
    skip: skip || !tenant,
    onData: ({ data: result }) => {
      if (result.data?.safetyEventCreated) {
        const update: RealTimeAlertUpdate = {
          type: 'created',
          alert: result.data.safetyEventCreated,
          isCritical: result.data.safetyEventCreated.severity === 'CRITICAL',
          timestamp: new Date(),
        };
        buffer.push(update);
        onAlertUpdate?.(update);
      }
    },
  });

  return {
    updates: buffer.events,
    clearUpdates: buffer.clear,
    latestUpdate: buffer.latest,
    criticalCount: buffer.events.filter((u) => u.isCritical).length,
    error: error ?? null,
    isConnected: !loading && !error,
  };
}

// ============================================================================
// NOTIFICATION SUBSCRIPTIONS
// ============================================================================

interface UseNotificationSubscriptionsOptions {
  channels?: string[];
  severityThreshold?: string;
  onNotification?: (notification: RealTimeNotification) => void;
  skip?: boolean;
  bufferSize?: number;
}

/**
 * Subscribe to notification updates in real-time.
 *
 * Matches backend resolver: FormsSubscription::notifications
 */
export function useNotificationSubscriptions(
  options: UseNotificationSubscriptionsOptions = {},
) {
  const { channels, severityThreshold, onNotification, skip = false, bufferSize } = options;
  const buffer = useEventBuffer<RealTimeNotification>(bufferSize);
  const [unreadCount, setUnreadCount] = useState(0);

  const { loading, error } = useSubscription<{
    notifications: NotificationEvent;
  }>(NOTIFICATION_SUBSCRIPTION, {
    variables: { channels, severityThreshold },
    skip,
    onData: ({ data: result }) => {
      if (result.data?.notifications) {
        const notification: RealTimeNotification = {
          notification: result.data.notifications,
          timestamp: new Date(),
        };
        buffer.push(notification);
        setUnreadCount((c) => c + 1);
        onNotification?.(notification);
      }
    },
  });

  const markAllRead = useCallback(() => {
    setUnreadCount(0);
  }, []);

  return {
    notifications: buffer.events,
    clearNotifications: buffer.clear,
    markAllRead,
    latestNotification: buffer.latest,
    unreadCount,
    error: error ?? null,
    isConnected: !loading && !error,
  };
}

// ============================================================================
// AUDIT SUBSCRIPTIONS
// ============================================================================

interface UseAuditSubscriptionsOptions {
  tenant?: string;
  eventTypes?: string[];
  onAuditEntry?: (audit: AuditEvent) => void;
  skip?: boolean;
  bufferSize?: number;
}

/**
 * Subscribe to audit event log entries in real-time.
 *
 * Matches backend resolver: FormsSubscription::audit_events
 * Uses Valkey Streams XREAD for pull-based streaming.
 */
export function useAuditSubscriptions(options: UseAuditSubscriptionsOptions = {}) {
  const { tenant, eventTypes, onAuditEntry, skip = false, bufferSize } = options;
  const buffer = useEventBuffer<AuditEvent>(bufferSize);

  const { loading, error } = useSubscription<{
    auditEvents: AuditEvent;
  }>(AUDIT_EVENT_SUBSCRIPTION, {
    variables: { tenant: tenant ?? '', eventTypes },
    skip: skip || !tenant,
    onData: ({ data: result }) => {
      if (result.data?.auditEvents) {
        buffer.push(result.data.auditEvents);
        onAuditEntry?.(result.data.auditEvents);
      }
    },
  });

  return {
    entries: buffer.events,
    clearEntries: buffer.clear,
    latestEntry: buffer.latest,
    entryCount: buffer.events.length,
    error: error ?? null,
    isConnected: !loading && !error,
  };
}

// ============================================================================
// WORKFLOW SUBSCRIPTIONS
// ============================================================================

interface UseWorkflowSubscriptionsOptions {
  tenant?: string;
  workflowName?: string;
  instanceId?: string;
  onStateTransition?: (event: SagaStateTransitionEvent) => void;
  skip?: boolean;
  bufferSize?: number;
}

/**
 * Subscribe to workflow and SAGA state transitions in real-time.
 *
 * Matches backend resolver: FormsSubscription::workflow_events
 */
export function useWorkflowSubscriptions(
  options: UseWorkflowSubscriptionsOptions = {},
) {
  const { tenant, workflowName, instanceId, onStateTransition, skip = false, bufferSize } = options;
  const statusBuffer = useEventBuffer<SagaStateTransitionEvent>(bufferSize);

  const { loading, error } = useSubscription<{
    workflowEvents: SagaStateTransitionEvent;
  }>(WORKFLOW_EVENT_SUBSCRIPTION, {
    variables: { tenant: tenant ?? '', workflowName, instanceId },
    skip: skip || !tenant,
    onData: ({ data: result }) => {
      if (result.data?.workflowEvents) {
        statusBuffer.push(result.data.workflowEvents);
        onStateTransition?.(result.data.workflowEvents);
      }
    },
  });

  const clearAll = useCallback(() => {
    statusBuffer.clear();
  }, [statusBuffer]);

  return {
    stateTransitions: statusBuffer.events,
    clearAll,
    latestTransition: statusBuffer.latest,
    error: error ?? null,
    isConnected: !loading && !error,
  };
}

// ============================================================================
// ACTOR SUBSCRIPTIONS
// ============================================================================

/** Actor update from real-time subscription */
export interface RealTimeActorUpdate {
  id: string;
  name: string;
  actorType: string;
  active: boolean;
  available: boolean;
  workload: number;
  maxCapacity: number;
  circuitBreakerOpen: boolean;
  circuitBreakerThreshold?: number;
  teamId?: string;
  updatedAt: number;
}

interface UseActorStatusSubscriptionOptions {
  actorId: string;
  onStatusChanged?: (actor: RealTimeActorUpdate) => void;
  skip?: boolean;
  bufferSize?: number;
}

/**
 * Subscribe to status changes for a specific actor.
 *
 * Matches backend resolver: ActorSubscription::actor_status_changed
 * Wired to Valkey pub/sub channel: ferp:{tenant}:Actor:{id}:status_changed
 */
export function useActorStatusSubscription(options: UseActorStatusSubscriptionOptions) {
  const { actorId, onStatusChanged, skip = false, bufferSize } = options;
  const buffer = useEventBuffer<RealTimeActorUpdate>(bufferSize);

  const { loading, error } = useSubscription<{
    actorStatusChanged: RealTimeActorUpdate;
  }>(ACTOR_STATUS_CHANGED_SUBSCRIPTION, {
    variables: { actorId },
    skip: skip || !actorId,
    onData: ({ data: result }) => {
      if (result.data?.actorStatusChanged) {
        buffer.push(result.data.actorStatusChanged);
        onStatusChanged?.(result.data.actorStatusChanged);
      }
    },
  });

  return {
    updates: buffer.events,
    clearUpdates: buffer.clear,
    latestUpdate: buffer.latest,
    error: error ?? null,
    isConnected: !loading && !error,
  };
}

interface UseActorTaskAssignedSubscriptionOptions {
  actorId: string;
  onTaskAssigned?: (actor: RealTimeActorUpdate) => void;
  skip?: boolean;
  bufferSize?: number;
}

/**
 * Subscribe to task assignments for a specific actor.
 *
 * Matches backend resolver: ActorSubscription::actor_task_assigned
 * Wired to Valkey pub/sub channel: ferp:{tenant}:Actor:{id}:task_assigned
 */
export function useActorTaskAssignedSubscription(options: UseActorTaskAssignedSubscriptionOptions) {
  const { actorId, onTaskAssigned, skip = false, bufferSize } = options;
  const buffer = useEventBuffer<RealTimeActorUpdate>(bufferSize);

  const { loading, error } = useSubscription<{
    actorTaskAssigned: RealTimeActorUpdate;
  }>(ACTOR_TASK_ASSIGNED_SUBSCRIPTION, {
    variables: { actorId },
    skip: skip || !actorId,
    onData: ({ data: result }) => {
      if (result.data?.actorTaskAssigned) {
        buffer.push(result.data.actorTaskAssigned);
        onTaskAssigned?.(result.data.actorTaskAssigned);
      }
    },
  });

  return {
    updates: buffer.events,
    clearUpdates: buffer.clear,
    latestUpdate: buffer.latest,
    error: error ?? null,
    isConnected: !loading && !error,
  };
}

interface UseActorCircuitBreakerSubscriptionOptions {
  onCircuitBreakerTriggered?: (actor: RealTimeActorUpdate) => void;
  skip?: boolean;
  bufferSize?: number;
}

/**
 * Subscribe to circuit breaker triggers for the tenant.
 *
 * Matches backend resolver: ActorSubscription::actor_circuit_breaker_triggered
 * Wired to Valkey pub/sub channel: ferp:{tenant}:Actor:circuit_breaker_triggered
 * Supports Formula #6 (Load-Balanced Queue) monitoring.
 */
export function useActorCircuitBreakerSubscription(options: UseActorCircuitBreakerSubscriptionOptions = {}) {
  const { onCircuitBreakerTriggered, skip = false, bufferSize } = options;
  const buffer = useEventBuffer<RealTimeActorUpdate>(bufferSize);

  const { loading, error } = useSubscription<{
    actorCircuitBreakerTriggered: RealTimeActorUpdate;
  }>(ACTOR_CIRCUIT_BREAKER_TRIGGERED_SUBSCRIPTION, {
    skip,
    onData: ({ data: result }) => {
      if (result.data?.actorCircuitBreakerTriggered) {
        buffer.push(result.data.actorCircuitBreakerTriggered);
        onCircuitBreakerTriggered?.(result.data.actorCircuitBreakerTriggered);
      }
    },
  });

  return {
    updates: buffer.events,
    clearUpdates: buffer.clear,
    latestUpdate: buffer.latest,
    triggeredCount: buffer.events.length,
    error: error ?? null,
    isConnected: !loading && !error,
  };
}

interface UseActorWorkloadSubscriptionOptions {
  onWorkloadChanged?: (actor: RealTimeActorUpdate) => void;
  skip?: boolean;
  bufferSize?: number;
}

/**
 * Subscribe to workload changes for the tenant.
 *
 * Matches backend resolver: ActorSubscription::actor_workload_changed
 * Wired to Valkey pub/sub channel: ferp:{tenant}:Actor:workload_changed
 * Supports Formula #6 (Load-Balanced Queue) real-time load balancing.
 */
export function useActorWorkloadSubscription(options: UseActorWorkloadSubscriptionOptions = {}) {
  const { onWorkloadChanged, skip = false, bufferSize } = options;
  const buffer = useEventBuffer<RealTimeActorUpdate>(bufferSize);

  const { loading, error } = useSubscription<{
    actorWorkloadChanged: RealTimeActorUpdate;
  }>(ACTOR_WORKLOAD_CHANGED_SUBSCRIPTION, {
    skip,
    onData: ({ data: result }) => {
      if (result.data?.actorWorkloadChanged) {
        buffer.push(result.data.actorWorkloadChanged);
        onWorkloadChanged?.(result.data.actorWorkloadChanged);
      }
    },
  });

  return {
    updates: buffer.events,
    clearUpdates: buffer.clear,
    latestUpdate: buffer.latest,
    error: error ?? null,
    isConnected: !loading && !error,
  };
}

// ============================================================================
// COMBINED REAL-TIME HOOK
// ============================================================================

interface UseRealTimeOptions {
  tenant?: string;
  enableTasks?: boolean;
  enableAlerts?: boolean;
  enableNotifications?: boolean;
  enableAudit?: boolean;
  enableWorkflows?: boolean;
  enableActors?: boolean;
  taskId?: string;
  instanceId?: string;
  workflowName?: string;
  severityThreshold?: string;
  auditEventTypes?: string[];
  /** Actor ID for status/task assignment subscriptions */
  actorId?: string;
}

/**
 * Combined hook for all real-time subscriptions.
 *
 * Selectively enables subscriptions based on options.
 * All use shared graphql-ws transport with reconnection.
 */
export function useRealTime(options: UseRealTimeOptions = {}) {
  const {
    tenant,
    enableTasks = true,
    enableAlerts = true,
    enableNotifications = true,
    enableAudit = false,
    enableWorkflows = false,
    enableActors = false,
    taskId,
    instanceId,
    workflowName,
    severityThreshold,
    auditEventTypes,
    actorId,
  } = options;

  const tasks = useTaskSubscriptions({
    taskId,
    instanceId,
    skip: !enableTasks,
  });
  const alerts = useAlertSubscriptions({
    tenant,
    severityThreshold,
    skip: !enableAlerts,
  });
  const notifications = useNotificationSubscriptions({
    skip: !enableNotifications,
  });
  const audit = useAuditSubscriptions({
    tenant,
    eventTypes: auditEventTypes,
    skip: !enableAudit,
  });
  const workflows = useWorkflowSubscriptions({
    tenant,
    workflowName,
    instanceId,
    skip: !enableWorkflows,
  });
  const actorStatus = useActorStatusSubscription({
    actorId: actorId ?? '',
    skip: !enableActors || !actorId,
  });
  const actorCircuitBreaker = useActorCircuitBreakerSubscription({
    skip: !enableActors,
  });
  const actorWorkload = useActorWorkloadSubscription({
    skip: !enableActors,
  });

  return {
    tasks,
    alerts,
    notifications,
    audit,
    workflows,
    actorStatus,
    actorCircuitBreaker,
    actorWorkload,
    hasLifeCriticalUpdates: tasks.lifeCriticalCount > 0 || alerts.criticalCount > 0,
    hasCircuitBreakerAlerts: actorCircuitBreaker.triggeredCount > 0,
    totalUnread: notifications.unreadCount,
  };
}
